<template>
	<div>
		<DoorsaleNavigation
			:events="events"
			v-model:searchText="searchText"
			v-model:selectedSpecialCategory="selectedSpecialCategory"
			v-model:selectedCategory="selectedCategory"
			v-model:selectedLocation="selectedLocation"
			v-model:selectedDate="selectedDate"
		/>

		<main class="py-8 mb-16">
			<BaseContainer>
				<div v-if="hasActiveFilters" class="mt-12 mb-3 flex flex-wrap gap-2">
					<button
						v-for="pill in activeFilterPills"
						:key="pill.label"
						type="button"
						class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-surface-light text-text border border-accent hover:bg-surface-grey-light transition-colors cursor-pointer"
						@click="pill.reset()"
					>
						<span>{{ pill.label }}</span>
						<CloseIcon class="w-3.5 h-3.5 fill-current flex-shrink-0" />
					</button>
				</div>
				<div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 mb-6" :class="hasActiveFilters ? '' : 'mt-12'">
					<h1 class="h2">{{ $t('doorsale.select_events') }}</h1>
					<div v-if="cart.cartExpire > 0" class="flex items-center gap-1 font-semibold text-sm flex-shrink-0">
						<StopwatchIcon class="fill-primary w-6 h-6" />
						<BaseCountdown :date="cart.cartExpire" /> {{ $t('checkout.countdown') }}
					</div>
				</div>
				<div class="flex flex-col md:flex-row gap-10">
					<!-- Event list -->
					<div class="flex-1 min-w-0">
						<!-- Loading state -->
						<div v-if="loading" class="flex justify-center py-16">
							<p class="text-text">{{ $t('doorsale.loading') }}</p>
						</div>

						<!-- Error state -->
						<div v-else-if="error" class="flex flex-col items-center py-16 gap-4 text-center">
							<p class="text-lg font-semibold text-error">{{ $t('doorsale.fetch_error') }}</p>
						</div>

						<template v-else>
							<!-- Empty results with active filters -->
							<div
								v-if="filteredEvents.length === 0 && hasActiveFilters"
								class="flex flex-col items-center justify-center py-16 gap-4 text-center"
							>
								<p class="text-lg font-semibold">{{ $t('doorsale.no_results') }}</p>
								<button
									type="button"
									class="btn btn-primary whitespace-nowrap inline-flex items-center justify-center border rounded-full font-semibold transition-colors cursor-pointer px-4 py-2 bg-surface-light text-text border-surface-grey hover:opacity-90"
									@click="resetFilters"
								>
									{{ $t('doorsale.reset_filters') }}
								</button>
							</div>

							<!-- No events from API -->
							<div
								v-else-if="events.length === 0"
								class="flex flex-col items-center justify-center py-16 gap-4 text-center"
							>
								<p class="text-lg font-semibold">{{ $t('doorsale.no_events') }}</p>
							</div>

							<!-- Event list -->
							<div v-else class="space-y-4">
								<DoorsaleEventCard
									v-for="event in filteredEvents"
									:key="event.id"
									:eventItem="event"
									:selected="doorsaleStore.selectedEventIds.includes(event.id)"
									@toggle="handleToggle(event.id)"
								/>
							</div>
						</template>
					</div>

					<!-- Summary sidebar -->
					<div class="flex-shrink-0 md:w-[50%] lg:w-[480px]">
					<DoorsaleEventSummary
						:events="events"
						@remove="handleToggle($event)"
						@remove-all="handleRemoveAll()"
					/>
				</div>
			</div>
			</BaseContainer>
		</main>

		<!-- Remove-event confirmation modal -->
		<BaseModal v-model="showConfirmModal" size="md" :must-confirm="true">
			<template #header><span class="md:whitespace-nowrap">{{ $t('doorsale.remove_event_cart_title') }}</span></template>
			<template #footer>
				<div class="flex flex-col md:flex-row gap-3">
					<BaseButton color="primary" size="lg" class="w-full" @click="confirmReset">
						{{ $t('doorsale.remove_event_cart_confirm') }}
					</BaseButton>
					<BaseButton color="default" size="lg" class="w-full" @click="showConfirmModal = false">
						{{ $t('doorsale.remove_event_cart_cancel') }}
					</BaseButton>
				</div>
			</template>
		</BaseModal>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, onUnmounted, ref } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRouter } from 'vue-router';

	import { fetchDoorsaleEvents } from '@/api/doorsaleEvents';
	import { UnauthorizedError } from '@/api/errors';
	import DoorsaleEventCard from '@/components/doorsale/DoorsaleEventCard.vue';
	import DoorsaleEventSummary from '@/components/doorsale/DoorsaleEventSummary.vue';
	import DoorsaleNavigation from '@/components/doorsale/DoorsaleNavigation.vue';
	import CloseIcon from '@/components/icons/CloseIcon.vue';
	import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
	import BaseButton from '@/components/ui/BaseButton.vue';
	import BaseContainer from '@/components/ui/BaseContainer.vue';
	import BaseCountdown from '@/components/ui/BaseCountdown.vue';
	import BaseModal from '@/components/ui/BaseModal.vue';
	import { useCartStore } from '@/stores/cart';
	import { useDoorsaleStore } from '@/stores/doorsaleStore';
	import { useGeneralStore } from '@/stores/general';
	import { useToastStore } from '@/stores/toastStore';
	import type { DoorsaleEvent } from '@/types';
	import { getEventtime } from '@/utils/eventItemUtil';
	import { formatDate } from '@/utils/formatDate';
	import { unixTimeToDate } from '@/utils/unixTimeToDate';
	import { useDoorsaleGestureNav } from '@/utils/useDoorsaleGestureNav';

	const { locale, t: $t } = useI18n();
	const router = useRouter();

	const doorsaleStore = useDoorsaleStore();
	const cart = useCartStore();
	const generalStore = useGeneralStore();

	// Block browser back (no safe destination from the event list) and wire
	// forward to the first step — design decision, not a technical constraint.
	useDoorsaleGestureNav(
		() => {},
		() => {
			if (doorsaleStore.selectedEventIds.length > 0)
				router.push({ name: 'doorsale-tickets', params: { eventId: doorsaleStore.selectedEventIds[0] } });
		},
	);
	const toastStore = useToastStore();

	const showEmptiedCartToast = localStorage.getItem('showEmptiedCartToast');
	if (showEmptiedCartToast == 'true') {
		toastStore.showToast($t('common.cart_emptied_toast'), 10000);
		localStorage.removeItem('showEmptiedCartToast');
	}

	const showConfirmModal = ref(false);

	const events = ref<DoorsaleEvent[]>([]);
	const loading = ref(true);
	const error = ref<string | null>(null);

	// Local filter state — does not pollute router.query or store
	const searchText = ref('');
	const selectedSpecialCategory = ref<string | null>(null);
	const selectedCategory = ref<string | null>(null);
	const selectedLocation = ref<string | null>(null);
	const selectedDate = ref<number | null>(null);

	const hasActiveFilters = computed(
		() => !!searchText.value || !!selectedSpecialCategory.value || !!selectedCategory.value || !!selectedLocation.value || !!selectedDate.value
	);

	const activeFilterPills = computed(() => {
		const pills: { label: string; reset: () => void }[] = [];
		if (searchText.value) {
			pills.push({ label: searchText.value, reset: () => { searchText.value = ''; } });
		}
		if (selectedSpecialCategory.value) {
			const val = selectedSpecialCategory.value;
			pills.push({ label: val, reset: () => { selectedSpecialCategory.value = null; } });
		}
		if (selectedCategory.value) {
			const val = selectedCategory.value;
			pills.push({ label: val, reset: () => { selectedCategory.value = null; } });
		}
		if (selectedLocation.value) {
			const val = selectedLocation.value;
			pills.push({ label: val, reset: () => { selectedLocation.value = null; } });
		}
		if (selectedDate.value) {
			const label = formatDate(selectedDate.value, { locale: locale.value });
			pills.push({ label, reset: () => { selectedDate.value = null; } });
		}
		return pills;
	});

	function resetFilters() {
		searchText.value = '';
		selectedSpecialCategory.value = null;
		selectedCategory.value = null;
		selectedLocation.value = null;
		selectedDate.value = null;
	}

	const filteredEvents = computed<DoorsaleEvent[]>(() => {
		let result = events.value;

		// 1. Text search: event name, ticket type names, formatted date
		if (searchText.value.trim()) {
			const term = searchText.value.trim().toLowerCase();
			result = result.filter(event => {
				if (event.info1.toLowerCase().includes(term)) return true;
				if (event.tickets.some(t => t.name1.toLowerCase().includes(term))) return true;
				const dateStr = event.eventtime_override
					? event.eventtime_override.toLowerCase()
					: formatDate(getEventtime(event), { locale: locale.value, timezone: event.tz_name || 'Europe/Copenhagen' }).toLowerCase();
				if (dateStr.includes(term)) return true;
				return false;
			});
		}

		// 2. Special category filter
		if (selectedSpecialCategory.value) {
			const matchingIds = generalStore.special_categories()
				.filter(sc => sc.name === selectedSpecialCategory.value)
				.map(sc => sc.id);
			result = result.filter(event =>
				event.special_categories?.some(id => matchingIds.includes(id))
			);
		}

		// 3. Category filter
		if (selectedCategory.value) {
			const matchingIds = generalStore.categories()
				.filter(cat => cat.name === selectedCategory.value)
				.map(cat => cat.id);
			result = result.filter(event =>
				event.categories?.some(id => matchingIds.includes(id))
			);
		}

		// 3. Location filter
		if (selectedLocation.value) {
			const venue = generalStore.venues().find(v => v.name === selectedLocation.value);
			if (venue) {
				result = result.filter(event => event.venue === venue.id);
			}
		}

		// 4. Date filter
		if (selectedDate.value) {
			const filterDate = unixTimeToDate(selectedDate.value);
			result = result.filter(event => {
				const eventDate = unixTimeToDate(getEventtime(event));
				return (
					filterDate.getUTCFullYear() === eventDate.getUTCFullYear() &&
					filterDate.getUTCMonth() === eventDate.getUTCMonth() &&
					filterDate.getUTCDate() === eventDate.getUTCDate()
				);
			});
		}

		return result;
	});

	function onKeydown(e: KeyboardEvent) {
		if (!showConfirmModal.value) return;
		if (e.key === 'Enter') {
			e.preventDefault();
			confirmReset();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			showConfirmModal.value = false;
		}
	}

	onMounted(async () => {
		window.addEventListener('keydown', onKeydown);
		try {
			const data = await fetchDoorsaleEvents();
			events.value = data.events;
		} catch (e) {
			if (e instanceof UnauthorizedError) {
				window.location.href = '/admin/login';
				return;
			}
			error.value = 'fetch_failed';
		} finally {
			loading.value = false;
		}
	});

	onUnmounted(() => {
		window.removeEventListener('keydown', onKeydown);
	});

	function handleToggle(eventId: number | string) {
		if (!doorsaleStore.selectedEventIds.includes(eventId)) {
			// Adding — no guard needed
			doorsaleStore.toggleEvent(eventId);
			return;
		}
		// Removing — check if this event has reserved tickets
		if (cart.items.some(item => String(item.id) === String(eventId))) {
			showConfirmModal.value = true;
		} else {
			doorsaleStore.toggleEvent(eventId);
		}
	}

	function handleRemoveAll() {
		if (cart.items.length > 0) {
			showConfirmModal.value = true;
		} else {
			doorsaleStore.clearEvents();
		}
	}

	function confirmReset() {
		doorsaleStore.clearEvents();
		cart.emptyCart();
		showConfirmModal.value = false;
	}
</script>
